import { connect } from "react-redux";

import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { isUnitIdEquals, SectionName, UnitId, unitWithoutInstance } from "../UnitId/UnitId";
import {
    addMultipleUnitInstanceAction,
    clearMultipleUnitInstanceAction,
    ClearPageMethod,
    copyMultipleUnitInstanceAction,
    removeMultipleUnitInstanceAction,
} from "../../../../Engine/ActionCreators/UnitActionCreators";
import { CandyDispatch } from "../../../../Engine/Core/Store/CandyReduxTypes";
import { AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { ViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { UnitIdResolver, withResolveUnitId } from "../../../withResolveUnitId";
import { withResolvePaths } from "../../../withResolvePaths";
import { withViewModelSelect } from "../../../withViewModelSelect";
import { CustomNormalizationHelper } from "../../../../Engine/Core/Normalization/CustomNormalizationHelper";
import { withCustomNormalizationHelper } from "../../../withCustomNormalizationHelper";
import { withFocusManagement } from "../../../Services/FocusManagement/withFocusManagement";
import { getRemoveLabelForChangeSet } from "../../UnitItem/UnitItemHelpers";

import { MultipleUnits } from "./MultipleUnits";
import { pagingForUnitsCount } from "./PagingForUnitsCount";

interface MultipleUnitsContainerProps {
    unitId: UnitId;
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    resolveUnitId: UnitIdResolver;
    modalUnitId?: UnitId;
    modalUnitTargetInstance?: string;
    customNormalizationHelper: CustomNormalizationHelper;
    unitsCountForPaging?: number;
    name?: string;
    modalTitle?: string;
    onRemoveUnit?: (instance: string, unitId: UnitId) => void;
    onClearUnit?: (instanceId: string, method: ClearPageMethod) => void;
}

const mapStateToProps = (state: FullFormState, props: MultipleUnitsContainerProps) => {
    const { bindingPath, viewModelSelect } = props;
    const instances = viewModelSelect.children(state, bindingPath);
    const currentUnit = state.highlightUnit;
    let currentInstance: string | undefined;
    if (
        instances.length > (props.unitsCountForPaging ?? pagingForUnitsCount) &&
        currentUnit != undefined &&
        isUnitIdEquals(unitWithoutInstance(currentUnit), props.unitId)
    ) {
        currentInstance = currentUnit.instance;
    }
    return {
        instances: instances,
        currentInstance: currentInstance,
        modalUnitId: props.modalUnitId,
        modalUnitTargetInstance: props.modalUnitTargetInstance,
        emptyUnits: state.emptyUnits,
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: MultipleUnitsContainerProps) => ({
    onAddMultipleUnitInstance: (newIndex?: number) => {
        if (ownProps.bindingPath != undefined) {
            dispatch(
                addMultipleUnitInstanceAction(ownProps.bindingPath, ownProps.resolveUnitId(ownProps.unitId), newIndex)
            );
        }
    },
    onRemoveMultipleUnitInstance: async (instance: string, unitId: UnitId) => {
        if (ownProps.bindingPath != undefined) {
            if (ownProps.onRemoveUnit) {
                ownProps.onRemoveUnit(instance, unitId);
            }

            dispatch(removeMultipleUnitInstanceAction(ownProps.bindingPath, instance, unitId, true));
            await dispatch(async (_, __, form) => {
                await form.changeSet.send({
                    operationName: getRemoveLabelForChangeSet(ownProps.modalTitle ?? ownProps.name),
                });
            });
        }
    },
    onCopyMultipleUnitInstance: async (instance: string, unitId: UnitId) => {
        if (ownProps.bindingPath != undefined) {
            await dispatch(copyMultipleUnitInstanceAction(ownProps.bindingPath, instance, unitId));
        }
    },
    onClearMultipleUnit: (instanceId: string, method: ClearPageMethod) => {
        if (ownProps.bindingPath != undefined) {
            if (ownProps.onClearUnit) {
                ownProps.onClearUnit(instanceId, method);
            }

            dispatch(
                clearMultipleUnitInstanceAction(
                    ownProps.unitId.pageId as SectionName,
                    ownProps.bindingPath,
                    instanceId,
                    method
                )
            );
        }
    },
    onEnableAutoValues: async (instance: string) => {
        if (ownProps.customNormalizationHelper.isFillAutoFieldsFeatureEnabled() && ownProps.bindingPath != undefined) {
            await ownProps.customNormalizationHelper.fillAutoFieldsWithAutoValuesAndReload([
                ownProps.bindingPath.joinWith(instance),
            ]);
        }
    },
});

export const MultipleUnitsContainer = withResolvePaths(
    withFocusManagement(
        withResolveUnitId(
            withViewModelSelect(
                withCustomNormalizationHelper(connect(mapStateToProps, mapDispatchToProps)(MultipleUnits))
            )
        )
    )
);
